/* eslint-disable no-nested-ternary */
import { useContext } from 'react';
import { Context as PurchaseTypeContext } from '@context/PurchaseTypeContext';
import {
  subscriptionTypes,
  subscriptionSubTypes,
  subscriptionSubTypesObj,
  getContentTypeName
} from '@constants/index';
import { businessPlatform } from '@utils/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  getIsSubscribedToGoal,
  getMeLatestUserSubscriptions,
  getPlusSubscriptionInfoLatest
} from '@api/selectors/subscription';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useReferralCodeStatusStore from '@stores/useReferralCodeStatus';
import { getSubscriptionTypeLabelFromType } from '@utils/subscriptionHelpers/getSubscriptionTypeForAnalytics';

const useCheckoutEventProps = (
  goalUID,
  subscription = {},
  isCombo,
  creditsApplied,
  isEMI = false
) => {
  const purchaseType = useContext(PurchaseTypeContext);
  const goal = useGoalInfo(goalUID);
  const {
    isReferralSuccessful: referralSuccessful,
    isReferralCodeApplied: referralCodeApplied
  } = useReferralCodeStatusStore();

  const getSubscriptionPlanTotal = () => {
    const key = creditsApplied ? 'tax_breakdown_with_credits' : 'tax_breakdown';
    return (
      subscription[key]?.find((item) => item.label === 'Total')?.amount || 0
    );
  };

  const endDate =
    subscription?.checkout_dates?.ends_at || subscription?.checkoutDates?.endsAt
      ? new Date(
          subscription?.checkout_dates?.ends_at ||
            subscription?.checkoutDates?.endsAt
        ).toISOString()
      : 'NA';

  const latestUserSubscriptions = useMyInfo(getMeLatestUserSubscriptions);
  const hasSubscription = useMyInfo(getPlusSubscriptionInfoLatest(goalUID));
  const hasActiveSubscription = useMyInfo(getIsSubscribedToGoal(goalUID));

  const hasIconicSubscription = !!latestUserSubscriptions?.find(
    (item) =>
      item.subscription?.value.uid === goalUID &&
      item.time_remaining > 0 &&
      item.subscription.type === 5
  );

  const subscriptionSubType = {};
  if (subscription.type === subscriptionTypes.CENTRE) {
    subscriptionSubType.business_platform = businessPlatform.UA_OFFLINE;
  }

  const getSubscriptionType = (type, contentTypeId) =>
    getSubscriptionTypeLabelFromType(type, contentTypeId);

  return {
    ...subscriptionSubType,
    purchase_type: purchaseType,
    goal_uid: goalUID,
    goal_name: goal.name || subscription?.goal?.name,
    subscription_duration: subscription.duration,
    is_subscription_active: hasActiveSubscription,
    is_upgrade:
      (!!hasSubscription &&
        !hasIconicSubscription &&
        subscription.type === subscriptionTypes.ICONIC) ||
      false,
    is_combo: isCombo,
    // Event changes here
    subscription_type: getSubscriptionType(
      subscription?.type,
      subscription?.contentTypeId
    ),
    entity_content_type: getContentTypeName(subscription?.contentTypeId),
    subscription_end_date: endDate,
    price: getSubscriptionPlanTotal(),
    is_referral_successful:
      referralSuccessful || !!subscription?.referral?.code,
    referral_code: referralCodeApplied || subscription?.referral?.code || 'NA',
    is_emi: isEMI,
    subscription_price: Math.round(subscription.price),
    batch_title: subscription.title,
    subscription_sub_type:
      subscriptionSubTypesObj[subscription.sub_type || subscription.subType] ||
      subscriptionSubTypesObj[subscriptionSubTypes.STANDARD]
  };
};

export default useCheckoutEventProps;
